import { useMemo } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import { defineMessages, useIntl } from 'react-intl';
import { type UseSearchResult } from '../../../hooks/use-search';
import { useIsFilterEnabled } from './useIsFilterEnabled';

export const useFilterButtons = (
    search: UseSearchResult,
): { id: string; label: string; count: number }[] => {
    const intl = useIntl();
    const isSmallDesktop = useBreakpointValue({ base: false, md: true, 'lg-sm': false });
    const isFilterVisible = useIsFilterEnabled();

    return useMemo(() => {
        const filterLabelsMap = {
            category: intl.formatMessage(filterLabels.category),
            price: intl.formatMessage(filterLabels.price),
            brand: intl.formatMessage(filterLabels.brand),
            material: intl.formatMessage(filterLabels.material),
            color: intl.formatMessage(filterLabels.color),
            size: intl.formatMessage(filterLabels.size),
            rating: intl.formatMessage(filterLabels.rating),
            saving: intl.formatMessage(filterLabels.saving),
        };

        return filterOrder
            .filter((filter) => {
                // Check if filter is visible based on feature flags
                if (!isFilterVisible(filter)) {
                    return false;
                }

                if (filter === 'category') {
                    return (search.filters?.categories?.length ?? 0) > 0;
                }

                if (filter === 'price') {
                    return true;
                }

                if (filter === 'brand') {
                    return (search.filters?.byName?.brand?.length ?? 0) > 0;
                }

                if (filter === 'material') {
                    return (search.filters?.byName?.material?.length ?? 0) > 0;
                }

                // On small desktop, hide non-price, non-category filters
                if (isSmallDesktop) {
                    return false;
                }

                if (filter === 'color') {
                    return (search.filters?.byName?.color?.length ?? 0) > 0;
                }

                if (filter === 'size') {
                    return (search.filters?.byName?.size?.length ?? 0) > 0;
                }

                return true;
            })
            .map((filter) => ({
                id: filter,
                label: filterLabelsMap[filter],
                count: search.activeFilters.filter((f) => f.type === filter).length,
            }));
    }, [
        intl,
        search.activeFilters,
        search.filters?.categories,
        search.filters?.byName,
        isSmallDesktop,
        isFilterVisible,
    ]);
};

export const filterOrder = [
    'category',
    'price',
    'brand',
    'material',
    'color',
    'size',
    'rating',
    'saving',
] as const;

const filterLabels = defineMessages({
    category: {
        id: 'search.filters.category',
        defaultMessage: 'Category',
    },
    price: {
        id: 'search.filters.price',
        defaultMessage: 'Price',
    },
    brand: {
        id: 'search.filters.brand',
        defaultMessage: 'Brand',
    },
    material: {
        id: 'search.filters.material',
        defaultMessage: 'Material',
    },
    color: {
        id: 'search.filters.color',
        defaultMessage: 'Color',
    },
    size: {
        id: 'search.filters.size',
        defaultMessage: 'Size',
    },
    rating: {
        id: 'search.filters.rating',
        defaultMessage: 'Rating',
    },
    saving: {
        id: 'search.filters.saving',
        defaultMessage: 'Discount',
    },
});
